import { Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { PriceSseService } from '../services/price-sse.service';
import { ProductService } from '../services/product.service';
import { applyLiveSalePrices as applyCartPrices } from './cart/cart.action';
import { selectCartItems } from './cart/cart.selector';
import { applyLiveSalePrices as applyProductPrices } from './product/product.action';
import { selectProductState } from './product/product.state';

/** Re-quotes SKUs currently on the cashier screen when a PriceChanged SSE arrives. */
@Injectable()
export class PriceLiveEffects {
  constructor(
    private readonly priceSse: PriceSseService,
    private readonly productService: ProductService,
    private readonly store: Store,
  ) {
    this.priceSse.connect();
  }

  applyQuotedPrices$ = createEffect(() =>
    this.priceSse.priceEvents.pipe(
      withLatestFrom(
        this.store.select(selectProductState),
        this.store.select(selectCartItems),
      ),
      switchMap(([event, productState, cartItems]) => {
        const visible = new Set(
          [...productState.products, ...productState.searchProducts, ...cartItems]
            .map((item) => item.skuCode?.trim().toUpperCase())
            .filter((sku): sku is string => !!sku),
        );
        const targets = event.skuCodes.filter((sku) =>
          visible.has(sku.trim().toUpperCase()),
        );
        if (!targets.length) {
          return EMPTY;
        }
        return forkJoin(
          targets.map((sku) =>
            this.productService.quoteSku(sku).pipe(
              map((response) => {
                const match = (response.pageContent ?? []).find(
                  (item) =>
                    item.skuCode?.trim().toUpperCase() === sku.trim().toUpperCase(),
                );
                if (!match?.skuCode || match.salesPrice == null) {
                  return null;
                }
                return { skuCode: match.skuCode, salesPrice: match.salesPrice };
              }),
              catchError(() => of(null)),
            ),
          ),
        ).pipe(
          mergeMap((rows) => {
            const prices = rows.filter(
              (row): row is { skuCode: string; salesPrice: number } => row != null,
            );
            if (!prices.length) {
              return EMPTY;
            }
            return of(applyProductPrices({ prices }), applyCartPrices({ prices }));
          }),
        );
      }),
    ),
  );
}
